'use client';

import CustomDivider from '@/components/custom-divider';
import { pageCustomerDirectories } from '@/constant/route';
import { useESurveyStore } from '@/hooks/use-esurvey-store';
import useMediaQuery from '@/hooks/use-media-query';
import { AllSubKeysMessageResource } from '@/i18n/messages';
import { cn } from '@/lib/utils';
import { Link, usePathname } from '@/navigation';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { screenSize } from '@/styles/styles';
import { ChevronDown, FileChartPie, UserRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Fragment, useMemo, useState } from 'react';
import {
    arrHeaderNavbar,
    arrSidebarNavbar,
    mediaBreakpoints,
} from '../../common';
import { useCustomerSidebarStore } from '../../store';
import { CustomerNavBar } from '../../type';
import { MenuIconOnly } from './menu-icon-only';

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────
export default function LeftSidebar({
    isSurveyEnabled = false,
}: {
    isSurveyEnabled?: boolean;
}) {
    const pathname = usePathname();
    const transSidebar = useTranslations('Sidebar');
    const authData = useAuthenticationStore((x) => x.auth);
    const { isShowSidebar } = useCustomerSidebarStore();

    /**
     * Đọc số khảo sát chưa thực hiện từ store toàn cục.
     * Store được cập nhật bởi CheckSurveyOnline ngay sau khi đăng nhập.
     */
    const pendingCount = useESurveyStore((x) => x.pendingCount);

    const isLargePC = useMediaQuery(`(max-width: ${screenSize['lg']}px)`);

    const menuToRender = useMemo(() => {
        let menus: CustomerNavBar[] = [];

        if (isLargePC) {
            menus.push({
                type: 'group',
                langResource: '' as any,
                children: arrHeaderNavbar,
            });
        }

        if (authData?.UserName) {
            // Item menu Khảo sát — badge hiển thị số chưa hoàn thành (nếu có)
            const surveyNavItem: CustomerNavBar = {
                langResource: 'Sidebar_Survey',
                icon: FileChartPie,
                href: pageCustomerDirectories.Survey,
                badge: pendingCount > 0 ? pendingCount : undefined,
            };

            // Thêm Survey vào cuối danh sách tài khoản nếu được phép
            const accountChildren = isSurveyEnabled
                ? [...arrSidebarNavbar, surveyNavItem]
                : arrSidebarNavbar;

            menus.push({
                type: 'group',
                icon: UserRound,
                langResource: 'Sidebar_Account',
                children: accountChildren,
            });
        }

        return menus;
    }, [authData?.UserName, isLargePC, isSurveyEnabled, pendingCount]);

    return (
        <div
            className={cn(
                'flex flex-col gap-y-1',
                isLargePC && 'my-2',
                isShowSidebar ? 'mr-2' : 'border-b-4 border-secondary pb-2'
            )}
        >
            {menuToRender.map((menu, idx) =>
                // ✅ Phân nhánh: group hay item thường
                menu.type === 'group' ? (
                    <NavGroup
                        key={idx}
                        menu={menu}
                        pathname={pathname}
                        transSidebar={transSidebar}
                        sidebarOpen={isShowSidebar}
                        isLargePC={isLargePC}
                    />
                ) : (
                    <div key={idx} className='flex flex-col gap-y-2'>
                        <NavItem
                            menu={menu}
                            pathname={pathname}
                            transSidebar={transSidebar}
                        />
                    </div>
                )
            )}
        </div>
    );
}

// ─────────────────────────────────────────────
// Sub-component: render 1 group (header + children items)
// ─────────────────────────────────────────────
function NavGroup({
    menu,
    pathname,
    transSidebar,
    sidebarOpen,
    isLargePC,
}: {
    menu: CustomerNavBar;
    pathname: string;
    transSidebar: ReturnType<typeof useTranslations<'Sidebar'>>;
    sidebarOpen: boolean;
    isLargePC?: boolean;
}) {
    const GroupIcon = menu.icon;

    return (
        <div className='mr-1'>
            {/* Group Header Label */}
            {!isLargePC && sidebarOpen && menu.langResource && (
                <div className='flex items-center gap-2 whitespace-nowrap px-1 pt-3'>
                    {GroupIcon && (
                        <div className='flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-secondary-foreground'>
                            <GroupIcon className='h-6 w-6' strokeWidth={2.5} />
                        </div>
                    )}
                    <span className='text-balance text-lg font-bold uppercase tracking-widest'>
                        {transSidebar(
                            menu.langResource as AllSubKeysMessageResource<'Sidebar'>
                        )}
                    </span>
                </div>
            )}

            {/* Group Items */}
            <div
                className={cn('flex flex-col gap-y-1', {
                    'border-secondary':
                        menu.children &&
                        menu.children.length > 0 &&
                        sidebarOpen &&
                        !isLargePC,
                    'ml-5 border-l-4 pb-3 pl-3': sidebarOpen && !isLargePC,
                })}
            >
                {menu.children?.map((child, i) => (
                    <NavItem
                        key={i}
                        menu={child}
                        pathname={pathname}
                        transSidebar={transSidebar}
                    />
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// Sub-component: render 1 item (có thể có children)
// ─────────────────────────────────────────────
function NavItem({
    menu,
    depth = 0,
    pathname,
    transSidebar,
}: {
    menu: CustomerNavBar;
    depth?: number;
    pathname: string;
    transSidebar: ReturnType<typeof useTranslations<'Sidebar'>>;
}) {
    const { isShowSidebar, setIsShowMenuOverlay } = useCustomerSidebarStore();
    const isDesktop = useMediaQuery(`(min-width: ${mediaBreakpoints}px)`);

    const [isOpen, setIsOpen] = useState(false);
    const Icon = menu.icon;
    const hasChildren = !!menu.children?.length;

    const isActive = pathname === menu.href;
    const isSubActive = menu.children?.some(
        (c) => c.href && pathname === c.href
    );

    const itemClassName = cn(
        'flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-md font-bold',
        'transition-colors duration-150',
        'hover:bg-secondary hover:text-secondary-foreground',
        isActive
            ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:text-secondary-foreground shadow-lg'
            : isSubActive
              ? 'bg-secondary/90 text-secondary-foreground hover:bg-secondary hover:opacity-80 hover:text-secondary-foreground shadow-md'
              : 'text-primary',
        depth > 0 && 'font-semibold',
        isShowSidebar ? 'justify-start' : 'justify-center'
    );

    const label = menu.langResource
        ? transSidebar(
              menu.langResource as AllSubKeysMessageResource<'Sidebar'>
          )
        : menu.name;

    const onAutoCloseMenuOverlay = () => {
        if (!isDesktop) {
            setIsShowMenuOverlay(false);
        }
    };

    const content = (
        <>
            {Icon && (
                <div
                    className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary group-hover:text-secondary-foreground',
                        { 'text-secondary-foreground': isActive }
                    )}
                >
                    <Icon strokeWidth={2.5} size={16} className='min-w-4' />
                </div>
            )}

            <span className='line-clamp-1 flex-1'>{label}</span>

            {/* Badge số lượng khảo sát chưa thực hiện — chỉ hiện khi sidebar mở */}
            {isShowSidebar && !!menu.badge && menu.badge > 0 && (
                <span
                    className={cn(
                        isActive
                            ? 'bg-secondary-foreground text-secondary'
                            : 'bg-secondary text-secondary-foreground group-hover:bg-secondary-foreground group-hover:text-secondary',
                        'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none'
                    )}
                >
                    {menu.badge > 99 ? '99+' : menu.badge}
                </span>
            )}

            {hasChildren && (
                <ChevronDown
                    strokeWidth={2.5}
                    size={16}
                    className={cn(
                        'transition-transform duration-200',
                        isOpen && 'rotate-180'
                    )}
                />
            )}
        </>
    );

    if (!isShowSidebar && isDesktop) {
        return <MenuIconOnly item={menu} pathname={pathname} />;
    }

    return (
        <div className='group'>
            {/* Render Link nếu có href, ngược lại render button */}
            {menu.href ? (
                hasChildren ? (
                    // Có cả href lẫn children → tách Link + button chevron
                    <div className='flex items-center gap-x-1'>
                        <Link
                            href={menu.href}
                            className={cn(itemClassName, 'flex-1')}
                            onClick={() => {
                                onAutoCloseMenuOverlay();
                            }}
                        >
                            {Icon && (
                                <Icon
                                    strokeWidth={2.5}
                                    size={16}
                                    className='min-w-4'
                                />
                            )}

                            <span className='line-clamp-1 flex-1'>{label}</span>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setIsOpen((p) => !p);
                                }}
                                className={cn('pl-2')}
                            >
                                <ChevronDown
                                    strokeWidth={2.5}
                                    size={16}
                                    className={cn(
                                        'min-w-4 transition-transform duration-200',
                                        isOpen && 'rotate-180'
                                    )}
                                />
                            </button>
                        </Link>
                    </div>
                ) : (
                    <Link
                        href={menu.href}
                        className={itemClassName}
                        onClick={() => {
                            onAutoCloseMenuOverlay();
                        }}
                    >
                        {content}
                    </Link>
                )
            ) : (
                <button
                    onClick={() => setIsOpen((p) => !p)}
                    className={cn(itemClassName, 'w-full text-left')}
                >
                    {content}
                </button>
            )}

            {/* Children */}
            {hasChildren && (
                <div
                    className={cn(
                        'overflow-hidden bg-slate-50 transition-all duration-200 ease-out',
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    )}
                >
                    <div className='ml-[18px] mt-0.5 flex flex-col gap-y-0.5 border-l-2 border-orange-100 pl-2'>
                        {menu.children!.map((child, i) => (
                            <NavItem
                                key={i}
                                menu={child}
                                depth={depth + 1}
                                pathname={pathname}
                                transSidebar={transSidebar}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
